import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { buildClassName } from '@bluebeam/build-class-name';
import { useDataAttributes } from '@bluebeam/react-hooks';
import { GeneralIcons } from '@bluebeam/react-skyline-art';
import { Label } from '@bluebeam/react-skyline-label';
import { Popover } from '@bluebeam/react-skyline-popover';
import { parseAbsoluteToLocal } from '@internationalized/date';
import { useRef } from 'react';
import { mergeProps, useButton, useDateRangePicker, useOverlay, useOverlayPosition, } from 'react-aria';
import { useDateRangePickerState } from 'react-stately';
import DateField from './DateField';
import RangeCalendar from './RangeCalendar';
const convertDateToLocal = (date) => {
    return parseAbsoluteToLocal(date.toISOString());
};
/**
 * A DateRangePickerField is a type of **molecule** allows users to select or enter a date range.
 */
const DateRangePickerField = ({ errorMessage, isDisabled, label, minDate, maxDate, ariaLabel, onChange, value, ...rest }) => {
    // Convert value, minDate, and maxDate to localized dates
    const localizedMinDate = minDate && convertDateToLocal(minDate);
    const localizedMaxDate = maxDate && convertDateToLocal(maxDate);
    const localizedSelectedDateRange = value && {
        start: convertDateToLocal(value.start),
        end: convertDateToLocal(value.end),
    };
    const state = useDateRangePickerState({
        errorMessage,
        label,
        minValue: localizedMinDate,
        maxValue: localizedMaxDate,
        onChange: (range) => {
            const start = range && range.start.toDate();
            const end = range && range.end.toDate();
            onChange && onChange(range && { start, end });
        },
        value: localizedSelectedDateRange,
    });
    const dateRangePickerContainerRef = useRef(null);
    const { groupProps, labelProps, buttonProps, startFieldProps, endFieldProps, dialogProps, calendarProps, errorMessageProps, } = useDateRangePicker({
        'aria-label': ariaLabel,
        granularity: 'day',
        label,
        isDisabled,
        minValue: localizedMinDate,
        maxValue: localizedMaxDate,
    }, state, dateRangePickerContainerRef);
    const calendarButtonRef = useRef(null);
    const { buttonProps: calendarButtonProps } = useButton({
        ...buttonProps,
        isDisabled,
    }, calendarButtonRef);
    const calendarPopoverRef = useRef(null);
    const { overlayProps } = useOverlay({
        isOpen: state.isOpen,
        isDismissable: true,
        onClose: state.close,
        shouldCloseOnBlur: true,
    }, calendarPopoverRef);
    const { overlayProps: positionProps } = useOverlayPosition({
        containerPadding: 0,
        isOpen: state.isOpen,
        onClose: state.close,
        offset: 2,
        overlayRef: calendarPopoverRef,
        targetRef: dateRangePickerContainerRef,
    });
    const dataAttributes = useDataAttributes(rest);
    return (_jsxs("div", { className: "inline-flex flex-col", ...dataAttributes, children: [label && (_jsx(Label, { ...labelProps, className: "mb-0.5", children: label })), _jsxs("div", { ...groupProps, className: buildClassName('border-darker-gray box-border h-10 w-72 rounded-lg border border-solid bg-white', 'focus-within:border-primary-blue focus-within:shadow-[0_0_0_1px_rgba(0,116,204)]', 'flex cursor-text items-center justify-evenly px-1', {
                    'border-light-gray cursor-default': isDisabled,
                    'focus-within:border-alerts-error-red border-alerts-error-red shadow-[0_0_0_1px_rgba(167,22,41)] focus-within:shadow-[0_0_0_1px_rgba(167,22,41)]': state.isInvalid && !state.isOpen,
                    'border-primary-blue shadow-[0_0_0_1px_rgba(0,116,204)]': state.isOpen,
                }), ref: dateRangePickerContainerRef, children: [_jsx(DateField, { ...startFieldProps, "aria-label": "start-date-field" }), _jsx("span", { className: buildClassName('text-darker-gray px-1 ', {
                            'text-light-gray': isDisabled,
                        }), children: "\u2013" }), _jsx(DateField, { ...endFieldProps, "aria-label": "end-date-field" }), _jsx("button", { className: "flex border-none bg-transparent p-0", ...calendarButtonProps, ref: calendarButtonRef, type: "button", children: state.isInvalid ? (_jsx(GeneralIcons.ErrorIcon, { className: "text-alerts-error-red h-6 w-6", size: "24px" })) : (_jsx(GeneralIcons.CalendarIcon, { size: "24px", className: buildClassName('cursor-pointer', {
                                'text-light-gray cursor-default': isDisabled,
                            }) })) })] }), errorMessage !== undefined && !state.isOpen && state.isInvalid && (_jsx("p", { ...errorMessageProps, className: "skyline-text-body-2 text-alerts-error-red m-0", children: errorMessage })), state.isOpen && (_jsx(Popover, { className: "elevation-3 overflow-y-auto", ...mergeProps(overlayProps, positionProps, dialogProps), ref: calendarPopoverRef, children: _jsx(RangeCalendar, { ...calendarProps }) }))] }));
};
export default DateRangePickerField;
//# sourceMappingURL=DateRangePickerField.js.map